import { useEffect, useState } from 'react';

import { HorizontalCarousel } from '../../views/listings/ListingsPage/components';
import { Container, Option, Title, WrapContainer } from './SelectOptions.styles';

export interface SelectOption<T> {
  selected: boolean;
  value: T;
  label: string;
}

interface SelectOptionsProps<T> {
  title?: string;
  options: SelectOption<T>[];
  multiselect?: boolean;
  onChange: (values: SelectOption<T>[]) => void;
  /**
   * If requireAtLeastOneAnswer is true, does not allow deselecting the only selected option
   */
  requireAtLeastOneAnswer?: boolean;
  /**
   * If wrapWhenOverflow is true, wraps the options to the next line when the options overflow the container
   */
  wrapWhenOverflow?: boolean;
  /**
   * If alternativeNoBackgroundStyles is true, uses alternative styles for the options
   */
  alternativeNoBackgroundStyles?: boolean;
}

const SelectOptions = <T,>({
  title,
  options,
  multiselect,
  onChange,
  requireAtLeastOneAnswer,
  wrapWhenOverflow,
  alternativeNoBackgroundStyles,
}: SelectOptionsProps<T>) => {
  const [selectOptions, setSelectOptions] = useState<Array<SelectOption<T>>>(options);

  useEffect(() => {
    // Updates local state when options change
    setSelectOptions(options);
  }, [options]);

  const handleSelection = (index: number) => {
    const newSelectOptions = selectOptions.map((option, i) => {
      if (!multiselect) {
        // If it is not multiselect, and requireAtLeastOneAnswer is true, does not allow deselecting the only selected option.
        if (
          requireAtLeastOneAnswer &&
          i === index &&
          option.selected &&
          selectOptions.filter((opt) => opt.selected).length === 1
        ) {
          return option;
        }
        // Else, leave only the newly selected option as the selected one.
        return i === index ? { ...option, selected: !option.selected } : { ...option, selected: false };
      }

      // If it is multiselect, and requireAtLeastOneAnswer is true, does not allow deselecting the only selected option.
      if (
        requireAtLeastOneAnswer &&
        i === index &&
        !option.selected &&
        selectOptions.filter((opt) => opt.selected).length === 0
      ) {
        return option;
      }

      // If it is just multiselect, toggle the value.
      return i === index ? { ...option, selected: !option.selected } : option;
    });

    setSelectOptions(newSelectOptions);
    onChange(newSelectOptions);
  };

  const Options = () => (
    <>
      {selectOptions.map((option, index) => (
        <Option
          key={index}
          onClick={() => handleSelection(index)}
          $selected={option.selected}
          $alternativeNoBackgroundStyles={alternativeNoBackgroundStyles}
        >
          {option.label}
        </Option>
      ))}
    </>
  );

  return (
    <Container>
      {title ? <Title>{title}</Title> : null}
      {wrapWhenOverflow ? (
        <WrapContainer>
          <Options />
        </WrapContainer>
      ) : (
        <HorizontalCarousel>{<Options />}</HorizontalCarousel>
      )}
    </Container>
  );
};

export default SelectOptions;
